import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  lineClampVariants,
  type LineClampVariantProps,
} from '@/hen-ui/atomics/LineClamp/line-clamp.variants';
import {
  InlineCopyBoundary,
  InlineCopyContext,
  PhrasingHostContext,
  INLINE_COPY_HOST_CLASS,
  inheritHostTypeScale,
  resolveNestedInlineTextTag,
  type InlinePhrasingTag,
} from '@/hen-ui/lib/inline-copy';
import { cn, warnOnce } from '@/hen-ui/utils';
import {
  textVariants,
  type TextColor,
  type TextVariantProps,
} from './text.variants';

export interface TextProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    TextVariantProps,
    LineClampVariantProps {
  asChild?: boolean;
  tag?: 'p' | 'div' | InlinePhrasingTag;
  variant?:
    | 'd1'
    | 'd2'
    | 'd3'
    | 'b1'
    | 'b2'
    | 'b3'
    | 'b4'
    | 'b5'
    | 'l1'
    | 'l2'
    | 'caption';
  /**
   * Semantic text color.
   *
   * `'accent'` reads the tenant's Theme v2 `colors.text.accent` role
   * (`--text-accent`). Without it, it falls back to the tenant's
   * `--accent-900`, then the platform accent default.
   *
   * `'neutral'` is a deprecated alias for `'subtext'` retained so existing
   * downstream consumers do not silently lose styling. New code should use
   * `'subtext'`; passing `'neutral'` triggers a one-time `console.warn` in
   * non-production builds.
   */
  color?: TextColor;
  /** Text alignment. */
  align?: 'left' | 'center' | 'right';
  /** Font weight. */
  weight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
  /** Visually hide the text while keeping it available to assistive technology. */
  visuallyHidden?: boolean;
  children: React.ReactNode;
}

/** Expansion fields removed from Text. Saved documents may still carry them. */
const LEGACY_EXPANSION_PROP_KEYS = [
  'expandable',
  'collapsedLabel',
  'expandedLabel',
  'trigger',
] as const;

function omitLegacyExpansionProps(
  props: React.HTMLAttributes<HTMLElement>,
): React.HTMLAttributes<HTMLElement> {
  const rest = { ...props } as Record<string, unknown>;
  for (const key of LEGACY_EXPANSION_PROP_KEYS) {
    delete rest[key];
  }
  return rest;
}

/**
 * General-purpose text with an optional visual line limit.
 *
 * `lines` only trims the text with an ellipsis. For a Show more / Show less
 * control, wrap the content in `LineClamp`.
 */
export const Text = React.forwardRef<HTMLElement, TextProps>(
  (
    {
      className,
      tag = 'p',
      variant,
      color,
      align,
      weight,
      visuallyHidden,
      lines = 'none',
      asChild = false,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    if (process.env.NODE_ENV !== 'production' && color === 'neutral') {
      warnOnce(
        'Text/color-neutral-deprecated',
        '[hen-ui/Text] `color="neutral"` is deprecated and aliases `"subtext"`. Update call sites to `color="subtext"`; the `neutral` value will be removed in the next major.',
      );
    }

    const nested = React.useContext(InlineCopyContext);
    const phrasingHost = React.useContext(PhrasingHostContext);
    const Tag = nested || phrasingHost ? resolveNestedInlineTextTag(tag) : tag;
    const Comp: React.ElementType = asChild ? Slot : Tag;
    const inheritHostType = {
      variant: inheritHostTypeScale(nested, variant),
      color: inheritHostTypeScale(nested, color),
      weight: inheritHostTypeScale(nested, weight),
    };
    return (
      <InlineCopyBoundary>
        <Comp
          ref={ref}
          className={cn(
            !nested && INLINE_COPY_HOST_CLASS,
            textVariants({
              ...inheritHostType,
              align,
              visuallyHidden,
            }),
            lineClampVariants({ lines }),
            className,
          )}
          style={
            nested
              ? { fontSize: 'inherit', lineHeight: 'inherit', ...style }
              : style
          }
          {...omitLegacyExpansionProps(props)}
          data-hen-id={COMPONENT_IDS.Text}
        >
          {children}
        </Comp>
      </InlineCopyBoundary>
    );
  },
);

Text.displayName = 'Text';
